import { AutocompleteMultiSelect, AutocompleteSingleSelect } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useTailoredSupplyReferences } from '../../hooks';
import {
  TailoredSupplyLanguage,
  type TailoredSupplyAppsFilter,
  type TailoredSupplyReference,
} from '../../model';

interface TailoredSupplyAppsFiltersProps {
  filter: TailoredSupplyAppsFilter;
  onChange: (filter: TailoredSupplyAppsFilter) => void;
}

const languageOptions = [
  { id: TailoredSupplyLanguage.English, label: 'English' },
  { id: TailoredSupplyLanguage.Spanish, label: 'Spanish' },
];

export function TailoredSupplyAppsFilters({ filter, onChange }: TailoredSupplyAppsFiltersProps) {
  const references = useTailoredSupplyReferences();

  return (
    <section
      aria-label={dashboardT('tailoredSupply.filters')}
      className="border-border bg-card grid min-w-0 gap-3 rounded-md border p-3 md:grid-cols-2 xl:grid-cols-4"
    >
      <AutocompleteMultiSelect
        label={dashboardT('tailoredSupply.publishersFilter')}
        options={toOptions(references.publishers.data ?? [])}
        value={filter.publisherIds}
        onValueChange={(publisherIds) => {
          onChange({ ...filter, publisherIds, page: 0 });
        }}
        loading={references.publishers.isFetching}
        placeholder={dashboardT('tailoredSupply.publishersFilter')}
      />
      <AutocompleteMultiSelect
        label={dashboardT('tailoredSupply.platformsFilter')}
        options={toOptions(references.platforms.data ?? [])}
        value={filter.platformIds}
        onValueChange={(platformIds) => {
          onChange({ ...filter, platformIds, page: 0 });
        }}
        loading={references.platforms.isFetching}
        placeholder={dashboardT('tailoredSupply.platformsFilter')}
      />
      <AutocompleteSingleSelect
        label={dashboardT('tailoredSupply.languageFilter')}
        options={languageOptions}
        value={filter.languages[0] ?? ''}
        onValueChange={(language) => {
          onChange({
            ...filter,
            languages: isTailoredSupplyLanguage(language) ? [language] : [],
            page: 0,
          });
        }}
        placeholder={dashboardT('tailoredSupply.languageFilter')}
      />
      <AutocompleteMultiSelect
        label={dashboardT('tailoredSupply.iabFilter')}
        options={toOptions(references.iabCategories.data ?? [])}
        value={filter.iabCategoryIds}
        onValueChange={(iabCategoryIds) => {
          onChange({ ...filter, iabCategoryIds, page: 0 });
        }}
        loading={references.iabCategories.isFetching}
        placeholder={dashboardT('tailoredSupply.iabFilter')}
      />
      {references.publishers.isError ||
      references.platforms.isError ||
      references.iabCategories.isError ? (
        <p role="alert" className="text-destructive text-xs md:col-span-2 xl:col-span-4">
          {dashboardT('tailoredSupply.lookupError')}
        </p>
      ) : null}
    </section>
  );
}

function isTailoredSupplyLanguage(language: string): language is TailoredSupplyLanguage {
  return Object.values<string>(TailoredSupplyLanguage).includes(language);
}

function toOptions(references: readonly TailoredSupplyReference[]) {
  return references.map((reference) => ({
    id: reference.id,
    label: reference.name,
  }));
}
